import * as React from 'react';
import type { DateRange } from 'react-day-picker';
import { format } from 'date-fns';
import { Filter, X } from 'lucide-react';
import CalendarRangePicker from '@/components/calendar-range-picker';
import type { DatePreset } from '@/components/calendar-range-picker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useHub } from '@/contexts/hub-context';
import { useDebouncedInput } from '@/hooks/use-debounced-input';
import type { CommunicationDisplay, MessageContent } from '../types/communications';
import { MessageInput } from '../components/message-input';
import { MessageList } from '../components/message-list';
import { ThreadPagination } from '../components/thread-pagination';

export type CenterPaneProps = {
  communications: CommunicationDisplay[];
  onMessageSelect: (messageId: string) => void;
  onSend: (content: MessageContent) => void;
  onUploadFiles: (files: File[]) => void;
  selectedMessageId?: string;
  isLoading?: boolean;
  isRefreshing?: boolean;
  onNextPage?: () => void;
  onPrevPage?: () => void;
  hasNextPage?: boolean;
  hasPrevPage?: boolean;
  isFetchingNextPage?: boolean;
};

export function CenterPane({
  communications,
  onMessageSelect,
  onSend,
  onUploadFiles,
  selectedMessageId,
  isLoading,
  isRefreshing,
  onNextPage,
  onPrevPage,
  hasNextPage,
  hasPrevPage,
  isFetchingNextPage,
}: CenterPaneProps) {
  const {
    isDealInbox,
    setSearchQuery,
    dateRangeFilter,
    setDateRangeFilter,
    clearDateRangeFilter,
    hasActiveDateFilter,
  } = useHub();

  const messageInputRef = React.useRef<HTMLDivElement>(null);
  const [messageInputHeight, setMessageInputHeight] = React.useState(0);
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const [localDateRange, setLocalDateRange] = React.useState<DateRange | undefined>(dateRangeFilter);
  const [showTooltip, setShowTooltip] = React.useState(false);

  // Debounced search hook
  const { value, debouncedValue, onChange, reset } = useDebouncedInput('', 500);

  // Format date range for display
  const formatDateRange = (range: DateRange | undefined): string => {
    if (!range?.from)
      return 'No date range selected';

    if (!range.to || range.from.getTime() === range.to.getTime()) {
      return format(range.from, 'MMM dd, yyyy');
    }

    return `${format(range.from, 'MMM dd, yyyy')} - ${format(range.to, 'MMM dd, yyyy')}`;
  };

  // Handle confirm button click
  const handleConfirmDateRange = React.useCallback(() => {
    setDateRangeFilter(localDateRange);
    setIsPopoverOpen(false);
  }, [localDateRange, setDateRangeFilter]);

  // Handle clear button click
  const handleClearDateRange = React.useCallback(() => {
    setLocalDateRange(undefined);
    clearDateRangeFilter();
    setIsPopoverOpen(false);
  }, [clearDateRangeFilter]);

  // Handle preset selection - auto-accept and close popover
  const handlePresetSelect = React.useCallback((preset: DatePreset) => {
    setDateRangeFilter(preset.value);
    setIsPopoverOpen(false);
  }, [setDateRangeFilter]);

  // Handle filter button click when search is active
  const handleFilterClick = React.useCallback(() => {
    if (value) {
      // Show tooltip briefly on click
      setShowTooltip(true);
      setTimeout(() => setShowTooltip(false), 2000);
      return;
    }
    setIsPopoverOpen(true);
  }, [value]);

  // Handle clear search button click
  const handleClearSearch = React.useCallback(() => {
    reset();
    setSearchQuery('');
  }, [reset, setSearchQuery]);

  // Observer to track message input height changes
  React.useEffect(() => {
    if (!messageInputRef.current)
      return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setMessageInputHeight(entry.contentRect.height);
      }
    });

    resizeObserver.observe(messageInputRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  // Update search query when debounced value changes
  React.useEffect(() => {
    setSearchQuery(debouncedValue);
  }, [debouncedValue]);

  // Sync local date range with context when popover opens
  React.useEffect(() => {
    if (isPopoverOpen) {
      setLocalDateRange(dateRangeFilter);
    }
  }, [isPopoverOpen, dateRangeFilter]);

  return (
    <div className="flex h-full flex-col">
      <Tabs defaultValue="all" className="w-full">
        <div className="flex justify-between gap-4 px-4 py-2">
          {isDealInbox // Search interface for Inbox view only
            ? (
                <h1 className="text-xl font-bold">Messages</h1>
              )
            : (
                <div className="relative max-w-sm flex-1">
                  <div className="space-y-1">
                    <Input
                      type="text"
                      placeholder={hasActiveDateFilter ? 'Clear date filter to search...' : 'Search Messages...'}
                      value={value}
                      onChange={onChange}
                      disabled={hasActiveDateFilter}
                      className={value ? 'pr-16' : 'pr-10'}
                    />
                  </div>
                  {/* Clear search button - only visible when there's search text */}
                  {value && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleClearSearch}
                            className="absolute right-9 top-1/2 size-6 -translate-y-1/2 p-0 hover:bg-muted"
                          >
                            <X className="size-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" align="center">
                          <p className="text-sm">Clear search</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  <TooltipProvider>
                    <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
                      <Tooltip open={value ? showTooltip : undefined}>
                        <TooltipTrigger asChild>
                          {value
                            ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={handleFilterClick}
                                  className={`absolute right-2 top-1/2 size-6 -translate-y-1/2 p-0 hover:bg-muted ${hasActiveDateFilter ? 'bg-ftld-secondary-100 text-secondary-foreground hover:bg-ftld-secondary-100/80' : ''
                                  }`}
                                >
                                  <Filter className="size-4" />
                                </Button>
                              )
                            : (
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className={`absolute right-2 top-1/2 size-6 -translate-y-1/2 p-0 hover:bg-muted ${hasActiveDateFilter ? 'bg-ftld-secondary-100 text-secondary-foreground hover:bg-ftld-secondary-100/80' : ''
                                    }`}
                                  >
                                    <Filter className="size-4" />
                                  </Button>
                                </PopoverTrigger>
                              )}
                        </TooltipTrigger>
                        <TooltipContent side="bottom" align="end">
                          <p className="text-sm">
                            {value
                              ? 'Clear search to enable date filter'
                              : (hasActiveDateFilter
                                  ? `Date Filter: ${formatDateRange(dateRangeFilter)}`
                                  : 'Filter by date range')}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                      <PopoverContent className="w-auto p-0" align="end">
                        <div className="flex flex-col">
                          <CalendarRangePicker
                            value={localDateRange}
                            onValueChange={setLocalDateRange}
                            onPresetSelect={handlePresetSelect}
                            showPresets={true}
                            numberOfMonths={1}
                          />
                          <div className="border-t p-4">
                            <div className="mb-3">
                              <p className="mb-1 text-sm font-medium text-muted-foreground">
                                Selected Range:
                              </p>
                              <p className="text-sm">
                                {formatDateRange(localDateRange)}
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={handleConfirmDateRange}
                                className="flex-1"
                              >
                                Confirm
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={handleClearDateRange}
                                className="flex-1"
                              >
                                Clear
                              </Button>
                            </div>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </TooltipProvider>
                </div>
              )}
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="internal">Internal</TabsTrigger>
            <TabsTrigger value="external">External</TabsTrigger>
          </TabsList>
        </div>
        <Separator />
        <TabsContent value="all" className="m-0">
          <MessageList
            communications={communications}
            isLoading={isLoading || isFetchingNextPage}
            isRefreshing={isRefreshing}
            onMessageSelect={onMessageSelect}
            selectedMessageId={selectedMessageId}
            messageInputHeight={messageInputHeight}
          />
        </TabsContent>
        <TabsContent value="internal" className="m-0">
          <MessageList
            communications={communications.filter((c) => !c.isExternal)}
            isLoading={isLoading || isFetchingNextPage}
            isRefreshing={isRefreshing}
            onMessageSelect={onMessageSelect}
            selectedMessageId={selectedMessageId}
            messageInputHeight={messageInputHeight}
          />
        </TabsContent>
        <TabsContent value="external" className="m-0">
          <MessageList
            communications={communications.filter((c) => c.isExternal)}
            isLoading={isLoading || isFetchingNextPage}
            isRefreshing={isRefreshing}
            onMessageSelect={onMessageSelect}
            selectedMessageId={selectedMessageId}
            messageInputHeight={messageInputHeight}
          />
        </TabsContent>
      </Tabs>
      {isDealInbox
        ? (
            <div className="mt-auto flex-none border-t px-4 pt-4" ref={messageInputRef}>
              <MessageInput onSend={onSend} onUploadFiles={onUploadFiles} />
            </div>
          )
        : (
            <div className="mt-auto flex-none border-t px-4 pb-2 pt-4" ref={messageInputRef}>
              <ThreadPagination
                onNextPage={onNextPage}
                onPrevPage={onPrevPage}
                hasNextPage={!!hasNextPage}
                hasPrevPage={!!hasPrevPage}
                isFetchingNextPage={isFetchingNextPage}
              />
            </div>
          )}
    </div>
  );
}
